/**
 * Read an initial list filter out of `?status=`, falling back to the page's own default.
 *
 * Owner, from production 2026-09-05: 这两个点击跳转到对应页面以后，并不是 pending 的过滤条件，而是 all 的.
 *
 * The Overview's pending card had just been fixed so each row's COUNT matches the page it opens
 * (`pendingCardDestinations.test.ts`). The page then opened on its own default — `all` on the two request
 * lists — so a worker clicking `3` landed on a list of everything and had to find the three. The
 * invariant goes one level deeper than the destination: a click on a count must land on the list THAT
 * COUNT DESCRIBES.
 *
 * Why a URL parameter rather than changing the defaults:
 *   - the SIDEBAR entry opens the same page and should keep showing everything. The web attendance list
 *     got its filter in the first place because "a worker with a year of leave history could not find the
 *     one row they were looking for" — that argues for `all` when the worker browses;
 *   - three of the six destinations already defaulted to `pending`, so the invariant held by COINCIDENCE.
 *     A parameter makes it structural on all six, which is what survives the next default change.
 *
 * SEED ONLY. The toolbar owns the value afterwards; syncing both ways would make the URL and the control
 * two sources of truth for one piece of state (svdy-rules §10.5b). This is also why it does not clear the
 * parameter the way the neighbouring `?compose=1` does — a filtered URL stays shareable and reloadable,
 * and the toolbar changing the view without rewriting the address is the ordinary behaviour of every list
 * that keeps its filter in the query string.
 *
 * Unknown values fall back to the default rather than being applied blindly: an unresolvable narrowing
 * input must not narrow to nothing (session-discipline §12).
 */
export function seedStatusFromUrl<T extends string>(fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  const raw = new URLSearchParams(window.location.search).get('status');
  if (!raw) return fallback;
  // The vocabularies differ per list (`pending|approved|rejected`, `pending|completed|all`,
  // `pending|reviewed`), and every one of them contains `pending` — which is the only value a count
  // link ever needs. Anything else is ignored rather than trusted into a state the page cannot render.
  return raw === 'pending' ? ('pending' as T) : fallback;
}
